import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, CalendarX, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common";
import { usePortal } from "@/pages/portal/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { DAYS, fmtMoney, fmtYmd } from "@/lib/format";
import { t } from "@/i18n";

const brandBtn = { background: "var(--brand)", color: "var(--brand-contrast)" };

export default function PortalLanding() {
  const { garage: g, services, slug } = usePortal();
  const { me } = useAuth();
  const c = g.contact || {};
  const addr = [c.address, c.suburb, c.state, c.postcode].filter(Boolean).join(", ");
  return (
    <div className="space-y-12" data-testid="portal-landing">
      <section className="relative overflow-hidden rounded-2xl p-8 sm:p-12" style={{ background: "var(--brand)", color: "var(--brand-contrast)" }}>
        <div className="gf-grid-bg absolute inset-0 opacity-30" />
        <div className="relative max-w-2xl gf-rise">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-80">{c.suburb ? `${c.suburb} ${c.state || ""}` : t("portal.welcome")}</p>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" data-testid="portal-hero-title">{g.name}</h1>
          <p className="mt-4 text-base opacity-90 md:text-lg">{t("portal.heroText")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {g.features?.online_booking && <Button asChild size="lg" variant="secondary" className="bg-white text-slate-900 hover:bg-white/90" data-testid="portal-hero-book-button"><Link to={`/g/${slug}/book`}>{t("book.cta")}</Link></Button>}
            {me ? (
              <Button asChild size="lg" variant="outline" className="border-current bg-transparent text-inherit hover:bg-white/10" data-testid="portal-hero-account-button"><Link to={`/g/${slug}/account`}>{t("portal.myAccount")}</Link></Button>
            ) : (
              <>
                <Button asChild size="lg" variant="outline" className="border-current bg-transparent text-inherit hover:bg-white/10" data-testid="portal-hero-signup-button"><Link to={`/g/${slug}/signup`}>{t("portal.createAccount")}</Link></Button>
                <Button asChild size="lg" variant="outline" className="border-current bg-transparent text-inherit hover:bg-white/10" data-testid="portal-hero-login-button"><Link to={`/g/${slug}/login`}>{t("portal.login")}</Link></Button>
              </>
            )}
          </div>
          <p className="mt-6 text-sm opacity-80" data-testid="portal-booking-notice">{t("portal.bookingSoon")}</p>
        </div>
      </section>

      <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <section>
          <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-slate-900"><Wrench className="h-5 w-5" style={{ color: "var(--brand)" }} /> {t("portal.services")}</h2>
          {services.length === 0 ? <p className="gf-card p-6 text-sm text-slate-600" data-testid="portal-no-services">{t("portal.noServices")}</p> : (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="portal-services-list">
              {services.map((s) => (
                <div key={s.id} className="gf-card p-5">
                  <p className="font-display text-lg font-bold text-slate-900">{s.name}</p>
                  {s.description && <p className="mt-1 text-sm text-slate-600">{s.description}</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-500">{t("portal.approxDuration", { d: s.duration_min })}</span>
                    <span className="flex items-center gap-2">
                      <StatusBadge status={s.price_type} />
                      {s.price_type !== "on_quote" && <strong className="text-sm">{s.price_type === "indicative" ? t("portal.from") : ""}{fmtMoney(s.price, g.currency)}</strong>}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        <aside className="space-y-6">
          <div className="gf-card p-6" data-testid="portal-contact-card">
            <h2 className="mb-4 text-lg font-bold text-slate-900">{t("portal.contact")}</h2>
            <ul className="space-y-3 text-sm text-slate-700">
              {addr && <li className="flex gap-2"><MapPin className="h-4 w-4 shrink-0 text-slate-400" /> {addr}</li>}
              {c.phone && <li className="flex gap-2"><Phone className="h-4 w-4 shrink-0 text-slate-400" /> <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a></li>}
              {c.email && <li className="flex gap-2"><Mail className="h-4 w-4 shrink-0 text-slate-400" /> <a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a></li>}
              {!addr && !c.phone && !c.email && <li className="text-slate-500">{t("portal.noContact")}</li>}
            </ul>
          </div>
          <div className="gf-card p-6" data-testid="portal-hours-card">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900"><Clock className="h-4 w-4" /> {t("portal.hours")}</h2>
            <ul className="space-y-1.5 text-sm">
              {DAYS.map((d) => (
                <li key={d} className="flex justify-between">
                  <span className="text-slate-600">{t(`days.${d}`)}</span>
                  <span className="font-medium text-slate-900">{g.hours?.[d]?.open ? `${g.hours[d].start} – ${g.hours[d].end}` : t("hours.closed")}</span>
                </li>
              ))}
            </ul>
            {g.closures?.length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500"><CalendarX className="h-3.5 w-3.5" /> {t("hours.closures")}</p>
                {g.closures.map((cl) => <p key={cl.date} className="text-sm text-slate-700">{fmtYmd(cl.date)} {cl.reason && `· ${cl.reason}`}</p>)}
              </div>
            )}
          </div>
        </aside>
      </div>
      <span className="hidden" style={brandBtn} />
    </div>
  );
}
